import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "./api/client";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SECTIONS = [
  { key: "teklif", label: "Teklifler" },
  { key: "reports", label: "Raporlar" },
  { key: "sozlesmeler", label: "Sözleşmeler" },
  { key: "isemri", label: "İş Emirleri (yazma dahil)" },
  {
    key: "is_emri_takvimi",
    label: "Takvim izleyici (salt okunur): yalnız İş Emri Takvimi + Tüm İş Emirleri — İş Emirleri kutusunu işaretleme",
  },
  { key: "musteriler", label: "Müşteriler" },
  { key: "makineler", label: "Makineler" },
  { key: "kullanicilar", label: "Kullanıcı Yönetimi" },
];

const COLUMNS = [
  { key: "id", label: "ID" },
  { key: "kullanici_adi", label: "Kullanıcı" },
  { key: "isim_soyisim", label: "Ad Soyad" },
  { key: "role", label: "Rol" },
  { key: "servis", label: "Web" },
  { key: "mobil_servis", label: "Mobil" },
  { key: "is_active", label: "Aktif" },
  { key: "takvim", label: "Takvim" },
  { key: "liste_goster", label: "Liste" },
  { key: "departman", label: "Departman" },
  { key: "sira_no", label: "Sıra" },
  { key: "tenant_id", label: "Müşteri no" },
  { key: "language", label: "Dil" },
];

const emptyForm = {
  kullanici_adi: "",
  isim_soyisim: "",
  email: "",
  password: "",
  role: "user",
  grup: "",
  servis: 1,
  mobil_servis: 0,
  is_active: true,
  takvim_only: false,
  takvim: false,
  liste_goster: false,
  departman: "Ofis",
  sira_no: "",
  tenant_id: "",
  countries: [],
  language: "",
};

function splitCountries(value) {
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

function CountryPicker({ options, value, onChange }) {
  const selected = value || [];
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
        <button type="button" className="btn btn-light btn-sm" onClick={() => onChange([])}>
          Temizle (tüm ülkeler)
        </button>
        <span style={{ fontSize: 12, color: "#666", alignSelf: "center" }}>
          {selected.length === 0 ? "Kısıt yok — tüm ülkeler" : `${selected.length} ülke seçili`}
        </span>
      </div>
      <select
        multiple
        className="form-control"
        style={{ minHeight: 96 }}
        value={selected}
        onChange={(e) => onChange(Array.from(e.target.selectedOptions, (option) => option.value))}
      >
        {options.map((name) => (
          <option key={name} value={name}>{name}</option>
        ))}
      </select>
    </div>
  );
}

function scopePayload(source, allCountries = []) {
  const tenant = source.tenant_id === "" || source.tenant_id == null ? null : Number(source.tenant_id);
  const tenantId = Number.isFinite(tenant) ? tenant : null;
  // Ülke kısıtı yalnız müşteri kapsamı (tenant) varken anlamlı.
  // Boş veya "hepsi seçili" → null (tüm ülkeler).
  let countries = null;
  if (tenantId != null) {
    const picked = Array.isArray(source.countries) ? source.countries.filter(Boolean) : [];
    const allSelected = allCountries.length > 0 && picked.length >= allCountries.length;
    countries = picked.length === 0 || allSelected ? null : picked.join(", ");
  }
  return {
    tenant_id: tenantId,
    countries,
    language: source.language || null,
  };
}

function textOf(row) {
  return [
    row.id,
    row.kullanici_adi,
    row.isim_soyisim,
    row.email,
    row.role,
    row.grup,
    row.departman,
    row.language,
    row.tenant_id,
  ]
    .map((v) => String(v ?? "").toLocaleLowerCase("tr"))
    .join(" ");
}

function compareRows(a, b, key, dir) {
  const mul = dir === "desc" ? -1 : 1;
  let va = a?.[key];
  let vb = b?.[key];

  if (key === "sira_no") {
    const aEmpty = va == null || va === "" || Number(va) <= 0;
    const bEmpty = vb == null || vb === "" || Number(vb) <= 0;
    if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
  } else {
    const aNull = va == null || va === "";
    const bNull = vb == null || vb === "";
    if (aNull !== bNull) return aNull ? 1 : -1;
  }

  if (typeof va === "boolean" || typeof vb === "boolean") {
    va = va ? 1 : 0;
    vb = vb ? 1 : 0;
  }

  const na = Number(va);
  const nb = Number(vb);
  if (va !== "" && vb !== "" && Number.isFinite(na) && Number.isFinite(nb)) {
    if (na !== nb) return (na < nb ? -1 : 1) * mul;
    return 0;
  }

  const sa = String(va ?? "").toLocaleLowerCase("tr");
  const sb = String(vb ?? "").toLocaleLowerCase("tr");
  return sa.localeCompare(sb, "tr") * mul;
}

function SortTh({ col, sort, onSort }) {
  const active = sort.key === col.key;
  const arrow = !active ? "" : sort.dir === "asc" ? " ▲" : " ▼";
  return (
    <th
      style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
      onClick={() => onSort(col.key)}
      title="Sırala"
    >
      {col.label}{arrow}
    </th>
  );
}

export default function KullaniciYonetimi() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [aktifFilter, setAktifFilter] = useState("");
  const [takvimFilter, setTakvimFilter] = useState("");
  const [departmanFilter, setDepartmanFilter] = useState("");
  const [sort, setSort] = useState({ key: "kullanici_adi", dir: "asc" });
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [pwd, setPwd] = useState("");
  const [permUser, setPermUser] = useState(null);
  const [permSections, setPermSections] = useState([]);
  const [busy, setBusy] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users", { params: { limit: 1000 } });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Kullanıcı listesi alınamadı (admin gerekli)");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/users/country-options").then((res) => {
      setCountryOptions(Array.isArray(res.data) ? res.data : []);
    }).catch(() => setCountryOptions([]));
  }, []);

  const departmanOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((u) => {
      const d = (u.departman || "").trim();
      if (d) set.add(d);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "tr"));
  }, [rows]);

  const visibleRows = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase("tr");
    let list = rows.filter((u) => {
      if (needle && !textOf(u).includes(needle)) return false;
      if (roleFilter && String(u.role || "") !== roleFilter) return false;
      if (aktifFilter === "1" && !u.is_active) return false;
      if (aktifFilter === "0" && u.is_active) return false;
      if (takvimFilter === "1" && !u.takvim) return false;
      if (takvimFilter === "0" && u.takvim) return false;
      if (departmanFilter && String(u.departman || "") !== departmanFilter) return false;
      return true;
    });
    list = [...list].sort((a, b) => compareRows(a, b, sort.key, sort.dir));
    return list;
  }, [rows, q, roleFilter, aktifFilter, takvimFilter, departmanFilter, sort]);

  const onSort = (key) => {
    setSort((prev) => (
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    ));
  };

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        servis: form.takvim_only ? 0 : Number(form.servis) || 1,
        mobil_servis: form.takvim_only ? 0 : Number(form.mobil_servis) || 0,
        password: form.takvim_only ? undefined : form.password || undefined,
        takvim: !!form.takvim,
        liste_goster: !!form.liste_goster,
        sira_no: form.sira_no === "" || form.sira_no == null ? null : Number(form.sira_no),
        ...scopePayload(form, countryOptions),
      };
      await api.post("/users", payload);
      toast.success("Kullanıcı oluşturuldu");
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Oluşturma hatası");
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await api.patch(`/users/${editing.id}`, {
        ...scopePayload(editing, countryOptions),
        isim_soyisim: editing.isim_soyisim,
        email: editing.email,
        role: editing.role,
        grup: editing.grup,
        servis: Number(editing.servis) || 0,
        mobil_servis: Number(editing.mobil_servis) || 0,
        is_active: !!editing.is_active,
        takvim: !!editing.takvim,
        liste_goster: !!editing.liste_goster,
        departman: editing.departman || "Ofis",
        sira_no: editing.sira_no === "" || editing.sira_no == null ? null : Number(editing.sira_no),
      });
      if (pwd) {
        await api.post(`/users/${editing.id}/password`, { password: pwd });
        setPwd("");
      }
      toast.success("Güncellendi");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Güncelleme hatası");
    } finally {
      setBusy(false);
    }
  };

  const openPerms = async (u) => {
    setPermUser(u);
    try {
      const { data } = await api.get(`/users/${u.id}/permissions`);
      const allowed = (data.sections || []).filter((s) => s.allowed).map((s) => s.section);
      setPermSections(allowed);
    } catch (_) {
      setPermSections([]);
    }
  };

  const savePerms = async () => {
    if (!permUser) return;
    setBusy(true);
    try {
      await api.put(`/users/${permUser.id}/permissions`, { sections: permSections });
      toast.success("Yetkiler kaydedildi");
      setPermUser(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yetki kaydı hatası");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: "0 auto" }}>
      <ToastContainer position="top-right" autoClose={2500} />
      <h4 style={{ marginBottom: 12 }}>Kullanıcı Yönetimi</h4>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16, alignItems: "center" }}>
        <input
          className="form-control"
          placeholder="Ara (ad, kullanıcı, departman...)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 260 }}
        />
        <select className="form-control" style={{ maxWidth: 140 }} value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">Rol: hepsi</option>
          <option value="user">user</option>
          <option value="admin">admin</option>
          <option value="superadmin">superadmin</option>
        </select>
        <select className="form-control" style={{ maxWidth: 140 }} value={aktifFilter}
          onChange={(e) => setAktifFilter(e.target.value)}>
          <option value="">Aktif: hepsi</option>
          <option value="1">Aktif</option>
          <option value="0">Pasif</option>
        </select>
        <select className="form-control" style={{ maxWidth: 150 }} value={takvimFilter}
          onChange={(e) => setTakvimFilter(e.target.value)}>
          <option value="">Takvim: hepsi</option>
          <option value="1">Takvimde</option>
          <option value="0">Takvimde değil</option>
        </select>
        <select className="form-control" style={{ maxWidth: 180 }} value={departmanFilter}
          onChange={(e) => setDepartmanFilter(e.target.value)}>
          <option value="">Departman: hepsi</option>
          {departmanOptions.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <button className="btn btn-primary btn-sm" type="button" onClick={load}>
          Yenile
        </button>
        <span style={{ fontSize: 12, color: "#666" }}>
          {visibleRows.length}/{rows.length}
        </span>
      </div>

      <form
        onSubmit={submitCreate}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))",
          gap: 8,
          background: "#f7f7f7",
          padding: 12,
          borderRadius: 6,
          marginBottom: 20,
        }}
      >
        <strong style={{ gridColumn: "1 / -1" }}>Yeni kullanıcı</strong>
        <input className="form-control" required placeholder="kullanici.adi" value={form.kullanici_adi}
          onChange={(e) => onChange("kullanici_adi", e.target.value)} />
        <input className="form-control" required placeholder="Ad Soyad" value={form.isim_soyisim}
          onChange={(e) => onChange("isim_soyisim", e.target.value)} />
        <input className="form-control" placeholder="E-posta" value={form.email}
          onChange={(e) => onChange("email", e.target.value)} />
        <input className="form-control" type="password" placeholder="Şifre" value={form.password}
          disabled={form.takvim_only}
          onChange={(e) => onChange("password", e.target.value)} />
        <input className="form-control" placeholder="Departman/grup" value={form.grup}
          onChange={(e) => onChange("grup", e.target.value)} />
        <select className="form-control" value={form.role} onChange={(e) => onChange("role", e.target.value)}>
          <option value="user">user</option>
          <option value="admin">admin</option>
          <option value="superadmin">superadmin</option>
        </select>
        <input className="form-control" type="number" min="0" max="2"
          placeholder="Web servis (0/1/2)" value={form.servis}
          onChange={(e) => onChange("servis", e.target.value)}
          title="0=yok 1=oku/yaz 2=sil" />
        <input className="form-control" type="number" min="0" max="2"
          placeholder="Mobil servis (0/1/2)" value={form.mobil_servis}
          onChange={(e) => onChange("mobil_servis", e.target.value)}
          title="Mobil uygulama: 0=yok 1=oku/yaz 2=not sil" />
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={form.takvim_only}
            onChange={(e) => onChange("takvim_only", e.target.checked)} />
          Login kapalı kayıt (servis=0)
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={!!form.takvim}
            onChange={(e) => onChange("takvim", e.target.checked)} />
          Takvimde personel olarak göster
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={!!form.liste_goster}
            onChange={(e) => onChange("liste_goster", e.target.checked)} />
          İş emri listesinde göster
        </label>
        <input className="form-control" placeholder="Takvim departmanı" value={form.departman}
          onChange={(e) => onChange("departman", e.target.value)} />
        <input className="form-control" type="number" placeholder="Takvim sıra no" value={form.sira_no}
          onChange={(e) => onChange("sira_no", e.target.value)} />
        <input className="form-control" type="number" placeholder="Müşteri no (musteri_id)" value={form.tenant_id}
          onChange={(e) => onChange("tenant_id", e.target.value)} />
        <select className="form-control" value={form.language} onChange={(e) => onChange("language", e.target.value)}>
          <option value="">Dil (boş = Türkçe, tam yetki)</option>
          <option value="tr">Türkçe</option>
          <option value="en">İngilizce</option>
        </select>
        {form.tenant_id !== "" && form.tenant_id != null && (
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>
              Ülke kısıtı (müşteri kapsamı için; boş = tüm ülkeler). Ctrl/Cmd ile çoklu seç.
            </div>
            <CountryPicker options={countryOptions} value={form.countries} onChange={(countries) => onChange("countries", countries)} />
          </div>
        )}
        <button className="btn btn-success" type="submit" disabled={busy}>
          Oluştur
        </button>
      </form>

      <div style={{ overflowX: "auto" }}>
        <table className="table table-sm table-striped" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <SortTh key={col.key} col={col} sort={sort} onSort={onSort} />
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((u) => (
              <tr key={u.id}>
                <td>{u.id}</td>
                <td>{u.kullanici_adi}</td>
                <td>{u.isim_soyisim}</td>
                <td>{u.role}</td>
                <td>{u.servis}</td>
                <td>{u.mobil_servis ?? 0}</td>
                <td>{u.is_active ? "Evet" : "Hayır"}</td>
                <td>{u.takvim ? "Evet" : "Hayır"}</td>
                <td>{u.liste_goster ? "Evet" : "Hayır"}</td>
                <td>{u.departman || ""}</td>
                <td>{u.sira_no ?? ""}</td>
                <td>{u.tenant_id ?? ""}</td>
                <td>{u.language || ""}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="btn btn-outline-primary btn-sm" type="button"
                    onClick={() => {
                      setEditing({
                        ...u,
                        departman: u.departman || "Ofis",
                        sira_no: u.sira_no ?? "",
                        tenant_id: u.tenant_id ?? "",
                        countries: splitCountries(u.countries),
                        language: u.language || "",
                      });
                      setPwd("");
                    }}>
                    Düzenle
                  </button>{" "}
                  <button className="btn btn-outline-secondary btn-sm" type="button"
                    onClick={() => openPerms(u)}>
                    Yetki
                  </button>
                </td>
              </tr>
            ))}
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length + 1} style={{ textAlign: "center", color: "#888" }}>
                  Kayıt yok
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,.35)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50,
        }}>
          <div style={{ background: "#fff", padding: 16, borderRadius: 8, width: 480, maxWidth: "95%" }}>
            <h5>Düzenle: {editing.kullanici_adi}</h5>
            <input className="form-control mb-2" value={editing.isim_soyisim}
              onChange={(e) => setEditing({ ...editing, isim_soyisim: e.target.value })} />
            <input className="form-control mb-2" value={editing.email || ""}
              onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
            <input className="form-control mb-2" value={editing.grup || ""}
              onChange={(e) => setEditing({ ...editing, grup: e.target.value })} />
            <select className="form-control mb-2" value={editing.role}
              onChange={(e) => setEditing({ ...editing, role: e.target.value })}>
              <option value="user">user</option>
              <option value="admin">admin</option>
              <option value="superadmin">superadmin</option>
            </select>
            <label className="d-block mb-1" style={{ fontSize: 12 }}>Web servis (0 yok / 1 oku-yaz / 2 sil)</label>
            <input className="form-control mb-2" type="number" min="0" max="2" value={editing.servis}
              onChange={(e) => setEditing({ ...editing, servis: e.target.value })} />
            <label className="d-block mb-1" style={{ fontSize: 12 }}>Mobil servis (0 yok / 1 oku-yaz / 2 not sil)</label>
            <input className="form-control mb-2" type="number" min="0" max="2" value={editing.mobil_servis ?? 0}
              onChange={(e) => setEditing({ ...editing, mobil_servis: e.target.value })} />
            <label className="d-block mb-2">
              <input type="checkbox" checked={!!editing.is_active}
                onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Aktif (login)
            </label>
            <label className="d-block mb-2">
              <input type="checkbox" checked={!!editing.takvim}
                onChange={(e) => setEditing({ ...editing, takvim: e.target.checked })} /> Takvimde göster
            </label>
            <label className="d-block mb-2">
              <input type="checkbox" checked={!!editing.liste_goster}
                onChange={(e) => setEditing({ ...editing, liste_goster: e.target.checked })} /> İş emri listesinde göster
            </label>
            <input className="form-control mb-2" placeholder="Takvim departmanı"
              value={editing.departman || ""}
              onChange={(e) => setEditing({ ...editing, departman: e.target.value })} />
            <input className="form-control mb-2" type="number" placeholder="Takvim sıra no"
              value={editing.sira_no ?? ""}
              onChange={(e) => setEditing({ ...editing, sira_no: e.target.value })} />
            <input className="form-control mb-2" type="number" placeholder="Müşteri no (musteri_id)"
              value={editing.tenant_id ?? ""}
              onChange={(e) => setEditing({ ...editing, tenant_id: e.target.value })} />
            <select className="form-control mb-2" value={editing.language || ""}
              onChange={(e) => setEditing({ ...editing, language: e.target.value })}>
              <option value="">Dil boş (Türkçe, tam yetki)</option>
              <option value="tr">Türkçe</option>
              <option value="en">İngilizce</option>
            </select>
            {(editing.tenant_id !== "" && editing.tenant_id != null) && (
              <div className="mb-2">
                <div style={{ fontSize: 12, marginBottom: 4 }}>
                  Ülke kısıtı (boş = tüm ülkeler). Ctrl/Cmd ile çoklu seç.
                </div>
                <CountryPicker
                  options={countryOptions}
                  value={editing.countries || []}
                  onChange={(countries) => setEditing({ ...editing, countries })}
                />
              </div>
            )}
            <input className="form-control mb-2" type="password" placeholder="Yeni şifre (opsiyonel)"
              value={pwd} onChange={(e) => setPwd(e.target.value)} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-light" type="button" onClick={() => setEditing(null)}>İptal</button>
              <button className="btn btn-primary" type="button" disabled={busy} onClick={saveEdit}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {permUser && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,.35)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50,
        }}>
          <div style={{ background: "#fff", padding: 16, borderRadius: 8, width: 380, maxWidth: "95%" }}>
            <h5>Menü yetkileri: {permUser.kullanici_adi}</h5>
            {SECTIONS.map((s) => (
              <label key={s.key} className="d-block mb-1">
                <input
                  type="checkbox"
                  checked={permSections.includes(s.key)}
                  onChange={(e) => {
                    setPermSections((prev) =>
                      e.target.checked ? [...prev, s.key] : prev.filter((x) => x !== s.key)
                    );
                  }}
                />{" "}
                {s.label}
              </label>
            ))}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button className="btn btn-light" type="button" onClick={() => setPermUser(null)}>İptal</button>
              <button className="btn btn-primary" type="button" disabled={busy} onClick={savePerms}>Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
